import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import {
  ActionButton,
  ActionLink,
  CloseButtonIcon,
  DesktopPopupModal,
  ExperiencesAvailabilityCard,
  MobilePopoverCard,
  useDeviceTypes,
} from "halifax";
import {
  ACTIVITIES_UNAVAILABLE_DATE_MODAL_OPENED,
  ACTIVITIES_UNAVAILABLE_DATE_MODAL_DISMISSED,
  ACTIVITIES_UNAVAILABLE_DATE_NEXT_DATE_SELECTED,
  ACTIVITIES_UNAVAILABLE_DATE_VIEW_ALTERNATIVES,
  CallState,
  ExperienceAvailability,
  ExperiencesShopOptionRequest,
} from "redmond";
import {
  getExperiencesAvailabilityQuery,
  goToExperiencesAvailability,
} from "@capone/common";

import styles from "./styles.module.scss";
import * as textConstants from "./textConstants";
import { UnavailableDateModalConnectorProps } from "./container";
import { getPaxMixFromTravelersCount } from "../../../common";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { PATH_AVAILABILITY } from "../../../../utils/paths";

export interface IUnavailableDateModalProps
  extends UnavailableDateModalConnectorProps {}

export const UnavailableDateModal = ({
  noAvailability,
  shopOptionsCallState,
  selectedDate,
  fromDate,
  untilDate,
  locationLabel,
  similarExperiences,
  rewardsKey,
  bookableItems,
  bookableItemDates,
  experienceId,
  travelerCounts,
  pricingOpaque,
  nextAvailableDate,
  nextAvailableDateCallState,
  findNextAvailableDate,
  setSelectedDate,
  activityDetailsProperties,
}: IUnavailableDateModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();
  const carouselRef = useRef<HTMLDivElement>(null);

  // Track the date that triggered the modal (selectedDate changes when user picks the next available date)
  const [unavailableDate, setUnavailableDate] = useState<string | null>(null);
  const hasProbed = useRef(false);
  // Prevent the modal from re-opening after user picks the next available date
  const dismissedRef = useRef(false);
  const shouldShow =
    noAvailability && shopOptionsCallState === CallState.Success;

  // Open the modal when noAvailability is detected
  useEffect(() => {
    if (shouldShow && selectedDate && !dismissedRef.current) {
      setUnavailableDate(selectedDate);
      setIsOpen(true);
      trackEvent({
        eventName: ACTIVITIES_UNAVAILABLE_DATE_MODAL_OPENED,
        properties: {
          ...activityDetailsProperties.properties,
          selected_date: selectedDate,
        },
        encryptedProperties: activityDetailsProperties.encryptedProperties,
      });
    }
  }, [shouldShow, selectedDate]);

  // Probe for next available date once the modal is open and we have the data we need
  useEffect(() => {
    if (
      !isOpen ||
      hasProbed.current ||
      !bookableItems?.length ||
      !experienceId ||
      !unavailableDate ||
      !bookableItemDates?.length
    ) {
      return;
    }

    hasProbed.current = true;

    const candidateDates = bookableItemDates.filter(
      (d) => d > unavailableDate,
    );

    if (candidateDates.length > 0) {
      const defaultCounts = { adultsCount: 1, childrenCount: 0, infantsCount: 0, seniorsCount: 0, youthsCount: 0 };
      const paxMix = getPaxMixFromTravelersCount(
        travelerCounts ?? defaultCounts,
      );
      const request: Omit<ExperiencesShopOptionRequest, "date"> = {
        experienceId,
        experienceOptionId: bookableItems[0].experienceOptionId,
        paxMix,
        currency: "USD",
        shopPricingOpaque: pricingOpaque,
        timeSlotPrices: [],
        ageBandPrices: [],
      };
      findNextAvailableDate(candidateDates, request);
    }
  }, [isOpen, bookableItems, bookableItemDates, experienceId, unavailableDate]);

  const handleClose = () => {
    dismissedRef.current = true;
    setIsOpen(false);
    trackEvent({
      eventName: ACTIVITIES_UNAVAILABLE_DATE_MODAL_DISMISSED,
      properties: {
        ...activityDetailsProperties.properties,
        selected_date: unavailableDate,
      },
      encryptedProperties: activityDetailsProperties.encryptedProperties,
    });
  };

  const handleSelectNextDate = () => {
    if (nextAvailableDate) {
      trackEvent({
        eventName: ACTIVITIES_UNAVAILABLE_DATE_NEXT_DATE_SELECTED,
        properties: {
          ...activityDetailsProperties.properties,
          unavailable_date: unavailableDate,
          next_available_date: nextAvailableDate,
        },
        encryptedProperties: activityDetailsProperties.encryptedProperties,
      });
      dismissedRef.current = true;
      setSelectedDate(nextAvailableDate);
      setIsOpen(false);
    }
  };

  const handleViewAlternatives = () => {
    trackEvent({
      eventName: ACTIVITIES_UNAVAILABLE_DATE_VIEW_ALTERNATIVES,
      properties: {
        ...activityDetailsProperties.properties,
        selected_date: unavailableDate,
      },
      encryptedProperties: activityDetailsProperties.encryptedProperties,
    });
    setIsOpen(false);
    // Preserve category/attraction filters from shopContext when going back
    let categoryTagId: string | null = null;
    try {
      const ctx = localStorage.getItem(
        `shop-context-${experienceId?.value}`,
      );
      if (ctx) {
        const shopCtx = JSON.parse(ctx);
        if (shopCtx.entryTag?.id) {
          const kind = shopCtx.entryTag?.kind?.ActivityTagKind === "Attraction" ? "_1" : "_0";
          categoryTagId = `${shopCtx.entryTag.id}${kind}`;
        }
      }
    } catch {
      // Ignore
    }
    if (categoryTagId && locationLabel) {
      const baseQuery = `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
        locationLabel,
        fromDate,
        untilDate,
      )}`;
      history.push(`${baseQuery}&categoryTagId=${categoryTagId}`);
    } else {
      goToExperiencesAvailability({
        history,
        locationLabel,
        fromDate,
        untilDate,
      });
    }
  };

  const searchedDateDisplay = unavailableDate
    ? textConstants.formatDateDisplay(unavailableDate)
    : "";

  const renderSimilarActivities = () => {
    if (!similarExperiences?.length) return null;

    return (
      <Box className={styles["similar-activities-carousel"]} {...{ ref: carouselRef } as any}>
        {similarExperiences.map(
          (exp: ExperienceAvailability, idx: number) => (
            <Box
              key={exp?.id?.value ?? idx}
              className={styles["similar-activity-card"]}
            >
              <ExperiencesAvailabilityCard
                experiencesAvailabilityInfo={exp}
                variant="card"
                isSkeleton={false}
                showCoverImageOnly
                rewardsKey={rewardsKey}
                onClick={() => {
                  if (exp?.id) {
                    setIsOpen(false);
                    const shopQuery = new URLSearchParams(
                      history.location.search,
                    );
                    shopQuery.set("activityId", exp.id.value);
                    history.push(`${history.location.pathname}?${shopQuery}`);
                  }
                }}
              />
            </Box>
          ),
        )}
      </Box>
    );
  };

  const modalContent = (
    <Box className={styles["unavailable-date-modal-content"]}>
      <Box className={styles["modal-header"]}>
        <Typography variant="h5" className={styles["modal-title"]}>
          {textConstants.MODAL_TITLE}
        </Typography>
      </Box>
      {nextAvailableDateCallState === CallState.InProcess ? (
        <Box className={styles["subtitle-loading"]}>
          The next available date is{" "}
          <span className={styles["loading-skeleton"]} />
          {" "}or check out similar activities available on{" "}
          {searchedDateDisplay} below:
        </Box>
      ) : (
        <Typography className={styles["modal-subtitle"]}>
          {nextAvailableDate ? (
            <>
              The next available date is{" "}
              <ActionLink
                className={styles["next-date-link"]}
                content={
                  <strong>
                    {textConstants.formatDateDisplay(nextAvailableDate)}
                  </strong>
                }
                onClick={handleSelectNextDate}
              />
              {" "}or check out similar activities available on{" "}
              {searchedDateDisplay} below:
            </>
          ) : (
            textConstants.SUBTITLE_WITHOUT_NEXT_DATE(searchedDateDisplay)
          )}
        </Typography>
      )}
      {renderSimilarActivities()}
      <Box className={styles["view-alternatives-container"]}>
        <ActionButton
          className={styles["view-alternatives-button"]}
          defaultStyle="h4r-primary"
          onClick={handleViewAlternatives}
          message={textConstants.VIEW_ALL_ALTERNATIVES}
        />
      </Box>
    </Box>
  );

  if (!isOpen) return null;

  if (matchesMobile) {
    return (
      <MobilePopoverCard
        open={isOpen}
        onClose={handleClose}
        className={styles["unavailable-date-modal-mobile"]}
        contentClassName={styles["unavailable-date-modal-mobile-content"]}
        topRightButton={
          <ActionLink
            content={<CloseButtonIcon />}
            label="Close"
            onClick={handleClose}
          />
        }
        centered
      >
        {modalContent}
      </MobilePopoverCard>
    );
  }

  return (
    <DesktopPopupModal
      open={isOpen}
      onClose={handleClose}
      className={styles["unavailable-date-modal-desktop"]}
      contentClassName={styles["unavailable-date-modal-desktop-content"]}
      invisibleBackdrop={false}
    >
      {modalContent}
    </DesktopPopupModal>
  );
};
